import {Injectable} from '@angular/core';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import {exportable$} from '../table/decorators/exportable';
import {Measure, MeasureRequest} from '../../models/measure';
import { HttpClient } from '@angular/common/http';
import {ParentTableService} from '../table/parent-table.service';
import {LinkingPotentialTargetMeasures} from '../../ui/dialogs/measure-link-dialog/models/linking-potential-target-measures';
import {map} from 'rxjs/operators';
import {ODataResponseMapper} from '../table/mappers/odata-response-mapper';
import {HelperFunctions} from '../table/configuration/helper-functions';
import {SQDataConfiguration} from '../table/configuration/OData-configuration';
import {TECH_BY_NUMBER} from '../technology/utils/constants';

@Injectable()
export class MeasureService extends ParentTableService {
  private odata: ODataEntitySetService<Measure>;
  private odataExt: ODataEntitySetService<Measure>;
  private config: SQDataConfiguration;
  @exportable$ measure: any;

  constructor(private odataFactory: ODataServiceFactory, private httpService: HttpClient) {
    super('MeasureService');
    this.odata = this.odataFactory.entitySet<Measure>('Overview(0)/Measure');
    this.odataExt = this.odataFactory.entitySet<Measure>('Overview(0)/ExtMeasure');
    this.config = new SQDataConfiguration();
  }

  getData(pageIndex, count, sortModel, filterModel) {
    const orders = [];
    sortModel.forEach(x => orders.push(x.colId.replace(/\./g, '/') + ' ' + x.sort));

    let filter = '';
    let dashboard = '';
    if (filterModel.chartCode) {
      dashboard = HelperFunctions.getDashboardFiltersQuery(filterModel.chartCode);
    }
    filter = HelperFunctions.addOdataFilters(filter, filterModel);

    const query = this.odata.entities().query(q => {
      q.top(count);
      q.skip(count * pageIndex);
      if (orders.length > 0) q.orderBy(orders);
      if (filter?.length) q.filter(filter);
      return q;
    });

    this.measure = query;
    return this.httpService.get(`${this.measure.api.serviceRootUrl + this.measure.toString()}&$count=true${dashboard}`);
  }

  getSupplierMeasures(id: number): any {
    return this.odata.entities().query(q => {
      q.filter(`SupplierId eq ${id}`);
      return q;
    }).fetch({ withCount: true }).pipe(map((r: any) => ODataResponseMapper.mapResponse(r)));
  }

  getSupplierMeasuresExternal(): any {
    return this.odataExt.entities().query(q => q).fetch({ withCount: true }).pipe(map((r: any) => ODataResponseMapper.mapResponse(r)));
  }

  addMeasure(measure: any) {
    measure['SpecialComment'] = null;
    measure['LinkedMeasureId'] = null;
    return this.httpService.post<Measure>(this.config.apiBaseUrl + 'Measure/Add()', this.removeNullValues(measure));
  }
  updateMeasure(id: number, measure: MeasureRequest) {
    return this.httpService.post<Measure>(this.config.apiBaseUrl + 'Measure/Update(' + id + ')', measure);
  }

  addToolboxMeasure(measure: any) {
    return this.httpService.post(this.config.apiBaseUrl + 'QualificationMeasure', measure);
  }

  deleteMeasure(id) {
    return this.httpService.delete(this.config.apiBaseUrl + 'Measure/' + id + '?testCaseStatusId=1');
  }

  getMeasureData(measureId: number) {
    return this.httpService.get<Measure>(this.config.apiBaseUrl + 'Measure/' + measureId);
  }

  linkMeasure(currentMeasure: Measure, measuresToLink: Measure) {
    const r = measuresToLink.Id;
    const request = this.measureToMeasureRequest(currentMeasure);
    request.LinkedMeasureId = r;
    return this.httpService.post(this.config.apiBaseUrl + 'Measure/Update(' + currentMeasure.Id + ')', this.removeNullValues(request));
  }

  unlinkMeasure(measureId: number, currentMeasure: MeasureRequest) {
    currentMeasure.LinkedMeasureId = null;
    return this.httpService.post(this.config.apiBaseUrl + 'Measure/Update(' + measureId + ')', currentMeasure);
  }

  getMeasurePotentialTarget(measureId: number) {
    return this.httpService.get<LinkingPotentialTargetMeasures>(this.config.apiBaseUrl + 'Measure/'
      + measureId + '/PotentialTargetMeasures');
  }
  getMeasurePotentialTargetTestCase(testCase: number) {
    return this.httpService.get<LinkingPotentialTargetMeasures>(this.config.apiBaseUrl + 'Measure/TestCaseResult/'
      + testCase + '/PotentialTargetMeasures');
  }

  shiftMeasure(measure: Measure) {
    return this.httpService.post(this.config.apiBaseUrl + 'Measure/Shift(' + measure.Id + ')', measure.Id);
  }

  measureToMeasureRequest(measure: any): MeasureRequest {
    const request: MeasureRequest = {
      StatusId: null,
      SupplierId: null,
      Title: null,
      TargetDate: null,
      ActualDate: null,
      SpecialComment: null,
      Description: null,
      Responsibility: null,
      Severity: null,
      Occurrence: null,
      Detection: null,
      IsVerified: true,
      VerificationDate: null,
      IsSupplier: true,
      IsTestCase: true,
      TestCaseResultId: null,
      LinkedMeasureId: null,
      Technology: null
    };
    Object.keys(request).forEach((key) => {
      request[key] = measure[key];
    });

    request.Technology = TECH_BY_NUMBER[request.Technology];
    return request;
  }
  removeNullValues(object: any): any {
    for (const key in object) {
      if (object[key] === null || object[key] === undefined) {
        delete object[key];
      }
    }
    return object;
  }
}
